'use client';

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Check, CheckCheck, Play, Pause } from 'lucide-react';
import ReplyPreview from './ReplyPreview';
import MediaMessage from './MediaMessage';
import ReactionBar from './ReactionBar';
import FileBubble from './FileBubble';
import { Message } from './types';

interface MessageBubbleProps {
  message: Message;
  isMe: boolean;
  isFirst: boolean;
  isLast: boolean;
  isSelecting: boolean;
  isSelected: boolean;
  onToggleSelect: (id: string) => void;
  onContextMenu: (msgId: string, x: number, y: number) => void;
  onReply: (msg: Message) => void;
  isEditing?: boolean;
  onSaveEdit?: (msgId: string, newText: string) => void;
  onCancelEdit?: () => void;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  const h = d.getUTCHours();
  const m = d.getUTCMinutes().toString().padStart(2, '0');
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${m} ${ampm}`;
}

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60).toString().padStart(2, '0');
  const s = (sec % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

function StatusIcon({ status }: { status: Message['status'] }) {
  if (!status) return null;
  if (status === 'sending') return <Check size={12} style={{ color: 'rgba(255,255,255,0.5)' }} aria-label="Sending" />;
  if (status === 'sent') return <Check size={12} style={{ color: 'rgba(255,255,255,0.5)' }} aria-label="Sent" />;
  if (status === 'delivered') return <CheckCheck size={12} style={{ color: 'rgba(255,255,255,0.55)' }} aria-label="Delivered" />;
  if (status === 'read') return <CheckCheck size={12} style={{ color: '#2EA6FF' }} aria-label="Read" />;
  return null;
}

// Audio bubble player
function AudioBubble({ audioUrl, duration, isMe, status, timestamp }: {
  audioUrl: string;
  duration?: number;
  isMe: boolean;
  status?: Message['status'];
  timestamp: string;
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentSec, setCurrentSec] = useState(0);
  const [totalSec, setTotalSec] = useState(duration ?? 0);

  useEffect(() => {
    const audio = new Audio(audioUrl);
    audioRef.current = audio;
    audio.playbackRate = 1.0;

    audio.onloadedmetadata = () => {
      if (isFinite(audio.duration) && audio.duration > 0) {
        setTotalSec(Math.round(audio.duration));
      } else {
        // WebM/Opus blobs from MediaRecorder often report Infinity duration.
        // Seek to a large value to force the browser to calculate the real duration.
        audio.currentTime = 1e101;
      }
    };

    audio.ontimeupdate = () => {
      // After seeking to 1e101 the browser snaps to the real end and updates duration
      if (!isFinite(audio.duration) || audio.duration === 0) return;
      if (audio.currentTime === audio.duration) {
        // We were seeking to get duration — reset to start
        setTotalSec(Math.round(audio.duration));
        audio.currentTime = 0;
        return;
      }
      setCurrentSec(Math.floor(audio.currentTime));
      setProgress(audio.duration ? audio.currentTime / audio.duration : 0);
    };
    audio.onended = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentSec(0);
    };

    return () => {
      audio.pause();
      audio.src = '';
    };
  }, [audioUrl]);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  }, [isPlaying]);

  const displaySec = isPlaying ? currentSec : totalSec;

  return (
    <div className="flex items-center gap-3 px-3 py-2.5" style={{ minWidth: '220px', maxWidth: '280px' }}>
      {/* Play/Pause button */}
      <button
        onClick={togglePlay}
        className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-90"
        style={{
          background: isMe ? 'rgba(255,255,255,0.2)' : 'rgba(46,166,255,0.25)',
        }}
        aria-label={isPlaying ? 'Pause voice message' : 'Play voice message'}
      >
        {isPlaying
          ? <Pause size={18} className="text-white" />
          : <Play size={18} className="text-white translate-x-[1px]" />
        }
      </button>

      {/* Waveform progress bar */}
      <div className="flex-1 flex flex-col gap-1.5">
        <div
          className="relative h-[3px] rounded-full overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.2)' }}
        >
          <div
            className="absolute inset-y-0 left-0 rounded-full transition-all duration-100"
            style={{
              width: `${progress * 100}%`,
              background: isMe ? 'rgba(255,255,255,0.85)' : '#2EA6FF',
            }}
          />
        </div>
        {/* Static waveform decoration */}
        <div className="flex items-end gap-[2px]">
          {[3,5,8,6,10,7,4,9,6,5,8,4,7,9,5,6,8,4,6,7,5,9,6,4,7,5,8,6].map((h, i) => (
            <div
              key={i}
              className="rounded-full flex-shrink-0"
              style={{
                width: '2px',
                height: `${h}px`,
                background: i / 28 <= progress
                  ? (isMe ? 'rgba(255,255,255,0.85)' : '#2EA6FF')
                  : 'rgba(255,255,255,0.25)',
              }}
            />
          ))}
        </div>
      </div>

      {/* Duration + timestamp + status */}
      <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
        <span className="text-[12px] font-mono font-medium tabular-nums" style={{ color: 'inherit', opacity: 0.8 }}>
          {formatDuration(displaySec)}
        </span>
        <span className="flex items-center gap-1 text-[11px] leading-none" style={{ color: 'var(--timestamp-color)', opacity: 0.75 }}>
          {formatTime(timestamp)}
          {isMe && <StatusIcon status={status} />}
        </span>
      </div>
    </div>
  );
}

export default function MessageBubble({
  message,
  isMe,
  isFirst,
  isLast,
  isSelecting,
  isSelected,
  onToggleSelect,
  onContextMenu,
  onReply,
  isEditing,
  onSaveEdit,
  onCancelEdit,
}: MessageBubbleProps) {
  const { id, text, imageUrl, imageAlt, imageWidth, imageHeight, audioUrl, audioDuration, fileUrl, fileName, fileSize, fileMime, timestamp, editedAt, status, replyTo, reactions, type } = message;

  const [editText, setEditText] = useState(text ?? '');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isEditing) {
      setEditText(text ?? '');
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          const len = textareaRef.current.value.length;
          textareaRef.current.setSelectionRange(len, len);
        }
      }, 50);
    }
  }, [isEditing, text]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (isEditing) return;
    e.preventDefault();
    onContextMenu(id, e.clientX, e.clientY);
  }, [id, onContextMenu, isEditing]);

  const handleClick = useCallback(() => {
    if (isSelecting) onToggleSelect(id);
  }, [isSelecting, id, onToggleSelect]);

  const handleSave = useCallback(() => {
    const trimmed = editText.trim();
    if (trimmed && trimmed !== text && onSaveEdit) {
      onSaveEdit(id, trimmed);
    } else if (onCancelEdit) {
      onCancelEdit();
    }
  }, [editText, text, id, onSaveEdit, onCancelEdit]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      onCancelEdit?.();
    }
  }, [handleSave, onCancelEdit]);

  const R = 18;
  const r = 5;

  let borderRadius: string;
  if (isMe) {
    const tl = R;
    const tr = isFirst ? R : r;
    const br = isLast ? r : r;
    const bl = R;
    borderRadius = `${tl}px ${tr}px ${br}px ${bl}px`;
  } else {
    const tl = isFirst ? R : r;
    const tr = R;
    const br = R;
    const bl = isLast ? r : r;
    borderRadius = `${tl}px ${tr}px ${br}px ${bl}px`;
  }

  const hasReactions = reactions && reactions.length > 0;
  const isImageOnly = type === 'image' && !text;
  const isAudio = type === 'audio' && audioUrl;
  const isFile = type === 'file' && fileUrl;

  return (
    <div
      className={`relative group flex ${isMe ? 'flex-row-reverse' : 'flex-row'} items-end gap-1 animate-fade-in-up w-full ${isSelected ? 'message-selected rounded-xl' : ''} transition-colors duration-150 ${hasReactions ? 'pb-2' : ''}`}
      onClick={handleClick}
    >
      {/* Selection checkbox */}
      {isSelecting && (
        <div className={`flex-shrink-0 flex items-center ${isMe ? 'order-last ml-1' : 'order-first mr-1'}`}>
          <div
            className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-150 ${isSelected ? 'border-[#2EA6FF]' : 'border-white/30 bg-transparent'}`}
            style={{ background: isSelected ? '#2EA6FF' : 'transparent' }}
          >
            {isSelected && (
              <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true">
                <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </div>
        </div>
      )}

      {/* Quick reply button */}
      {!isSelecting && !isEditing && (
        <button
          onClick={(e) => { e.stopPropagation(); onReply(message); }}
          className={`opacity-0 group-hover:opacity-100 flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150 hover:bg-white/15 active:scale-90 order-last`}
          style={{ background: 'rgba(255,255,255,0.08)' }}
          title="Reply"
          aria-label="Reply to this message"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="9 17 4 12 9 7" />
            <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
          </svg>
        </button>
      )}

      {/* Bubble */}
      <div
        className={`relative max-w-full shadow-bubble cursor-pointer select-none ${isMe ? 'bubble-out' : 'bubble-in'}`}
        style={{ borderRadius }}
        onContextMenu={handleContextMenu}
        role="article"
        aria-label={`${isMe ? 'You' : 'Ate Boni'}: ${isAudio ? 'Voice message' : text || 'image'}`}
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' && !isEditing) handleContextMenu(e as unknown as React.MouseEvent); }}
      >
        {/* Reply preview */}
        {replyTo && <ReplyPreview message={replyTo} isMe={isMe} />}

        {/* Audio bubble */}
        {isAudio && (
          <AudioBubble
            audioUrl={audioUrl}
            duration={audioDuration}
            isMe={isMe}
            status={status}
            timestamp={timestamp}
          />
        )}

        {/* File bubble */}
        {isFile && (
          <FileBubble
            fileUrl={fileUrl}
            fileName={fileName ?? 'File'}
            fileSize={fileSize}
            fileMime={fileMime}
            isMe={isMe}
            status={status}
            timestamp={timestamp}
          />
        )}

        {/* Image */}
        {imageUrl && (
          <MediaMessage
            src={imageUrl}
            alt={imageAlt || 'Shared image'}
            width={imageWidth || 400}
            height={imageHeight || 280}
            hasText={!!text}
            borderRadius={borderRadius}
            timestamp={timestamp}
            isMe={isMe}
            status={status}
          />
        )}

        {/* Inline edit mode */}
        {isEditing && isMe ? (
          <div className="px-3 pt-2.5 pb-2 flex flex-col gap-2 min-w-[220px]">
            <textarea
              ref={textareaRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={Math.min(6, Math.max(1, editText.split('\n').length))}
              className="w-full resize-none rounded-lg px-2 py-1.5 text-[15px] leading-[1.5] outline-none border border-[#2EA6FF]/60 focus:border-[#2EA6FF]" style={{ color: 'inherit' }}
              style={{ background: 'rgba(46,166,255,0.1)', minWidth: '180px' }}
              aria-label="Edit message"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={onCancelEdit}
                className="text-[12px] px-3 py-1 rounded-full font-medium transition-colors"
                style={{ color: 'rgba(215,224,234,0.7)', background: 'rgba(255,255,255,0.08)' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.14)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.08)'; }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="text-[12px] px-3 py-1 rounded-full font-medium transition-colors"
                style={{ color: '#fff', background: '#2EA6FF' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#1a8fe0'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = '#2EA6FF'; }}
              >
                Save
              </button>
            </div>
          </div>
        ) : (
          /* Normal text display — skip for audio-only */
          !isAudio && text && (
            <div className={`px-3 ${imageUrl ? 'pt-1 pb-1' : 'pt-1.5 pb-1'} flex flex-wrap items-end gap-x-1.5 gap-y-0`}>
              <p className="text-[13px] leading-[1.4] break-words whitespace-pre-wrap flex-1 min-w-0" style={{ color: 'inherit' }}>
                {text}
              </p>
              <span className="flex items-center gap-1 text-[10px] leading-none flex-shrink-0 self-end ml-auto pl-1" style={{ color: 'var(--timestamp-color)', opacity: 0.75 }}>
                {editedAt && (
                  <span className="italic" aria-label="Message was edited">edited</span>
                )}
                {formatTime(timestamp)}
                {isMe && <StatusIcon status={status} />}
              </span>
            </div>
          )
        )}

        {/* Timestamp for image-only messages is handled inside MediaMessage */}

        {/* Reactions */}
        {hasReactions && (
          <div
            className={`absolute z-10 ${isMe ? 'left-2' : 'right-2'}`}
            style={{ bottom: '-8px' }}
          >
            <ReactionBar reactions={reactions!} isMe={isMe} />
          </div>
        )}
      </div>
    </div>
  );
}